import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api";

// Código único de cada envío: con él la pantalla consulta qué paso lleva la IA mientras responde.
export function newRequestId() {
  try {
    return crypto.randomUUID();
  } catch (err) {
    return `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
  }
}

// El panel oculta las credenciales pegadas ANTES de guardar el mensaje; este filtro hace lo mismo en pantalla: un texto que
// parezca traer contraseñas, claves, llaves o una IP NO se muestra de inmediato (se verá ya ocultado cuando quede guardado).
const SENSITIVE = /contrase|passw|\bpass\b|\bpwd\b|clave|secret|token|api[_ -]?key|private key|\b(?:\d{1,3}\.){3}\d{1,3}\b|:\/\/[^\s/:@]+:[^\s@]+@/i;
export function looksLikeCredentials(text) {
  return typeof text === "string" && SENSITIVE.test(text);
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, message, conversationId, attachmentIds, requestId }) => {
      const { data } = await apiClient.post("/chat", {
        project_id: projectId,
        message,
        conversation_id: conversationId,
        attachment_ids: attachmentIds || [],
        request_id: requestId,
      });
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["conversations", variables.projectId] });
    },
  });
}

// ¿Hay una tarea de la IA en curso en esta conversación? Sirve para volver a engancharse al recargar la página; mientras
// `watch` es verdadero sigue consultando (cada 4 s) para saber cuándo terminó.
export function useRunningJob(conversationId, watch = false) {
  return useQuery({
    queryKey: ["chat-job", conversationId],
    queryFn: async () => (await apiClient.get(`/chat/jobs/by-conversation/${conversationId}`)).data,
    enabled: !!conversationId,
    refetchInterval: watch ? 4000 : false,
    retry: false,
    gcTime: 0,
  });
}

// Detener la tarea en curso (se revisa entre un paso y el siguiente).
export async function cancelJob(requestId) {
  const { data } = await apiClient.post(`/chat/jobs/${requestId}/cancel`);
  return data;
}

// Pasos que lleva la IA en la respuesta en curso (solo mientras se espera).
export function useChatProgress(requestId, enabled) {
  return useQuery({
    queryKey: ["chat-progress", requestId],
    queryFn: async () => (await apiClient.get(`/chat/progress/${requestId}`)).data,
    enabled: !!requestId && !!enabled,
    refetchInterval: enabled ? 1500 : false,
    retry: false,
    gcTime: 0,
  });
}
